import { Injectable, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  ThasActor,
  ThasClient,
  ThasClientEnvironment,
  ThasClientOptions,
  ThasEvent,
  ThasNavigationEvent,
  ThasViewEvent,
} from '@thiememeulenhoff/analytics-service-client';
import { BehaviorSubject, filter, interval, Subscription } from 'rxjs';

export class AnalyticsServiceConfig implements ThasClientOptions {
  public token = '';
  public environment?: ThasClientEnvironment;
  public connectToRouter? = true;
  public heartbeatEventsEnabled? = true;
  public heartbeatInterval? = 60000;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  readonly #client: ThasClient;

  readonly #eventsSubject = new BehaviorSubject<Array<ThasEvent>>([]);
  public readonly events$ = this.#eventsSubject.asObservable();

  #heartbeat: Subscription | null = null;
  #heartbeatInterval = 60000;
  #heartbeatEventsEnabled = true;
  #previousUrl: string | null = null;

  constructor(
    @Optional() config: AnalyticsServiceConfig,
    private router: Router
  ) {
    this.#heartbeatInterval =
      config?.heartbeatInterval || this.#heartbeatInterval;
    this.#heartbeatEventsEnabled =
      config?.heartbeatEventsEnabled || this.#heartbeatEventsEnabled;

    this.#client = this.#getThasClient(config);

    this.#connectRouter();
    this.#connectEvents();
  }

  public send(event: ThasEvent): void {
    this.#client.send(event);
  }

  /**
   * Set the current actor for the client
   * @param actor `ThasActor`
   */
  public setActor(actor?: ThasActor): void {
    this.#client.setActor(actor);
  }

  public getActor(): ThasActor | undefined {
    return this.#client.getActor();
  }

  /**
   * Set the current arrangement code for the client
   * @param actor `ThasActor`
   */
  public setArrangementCode(actor: string): void {
    this.#client.setArrangementCode(actor);
  }

  public getArrangementCode(): string | undefined {
    return this.#client.getArrangementCode();
  }

  /**
   * Clear the stream of events
   */
  public clear(): void {
    this.#eventsSubject.next([]);
  }

  /**
   * Connect custom thasEvent to observable stream of events
   */
  #connectEvents(): void {
    this.#client.subscribeToEvents((event) => {
      const items = this.#eventsSubject.getValue();
      this.#eventsSubject.next([event, ...items]);
    });
  }

  /**
   * Connects Angular route change to `ThasViewEvent`
   */
  #connectRouter(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const visit = new ThasNavigationEvent(
          'navigatedTo',
          this.router.url,
          this.#previousUrl ?? undefined
        );
        this.#client.send(visit);

        this.#previousUrl = this.router.url;

        if (this.#heartbeatEventsEnabled) {
          this.#heartbeat?.unsubscribe();
          this.#heartbeat = interval(this.#heartbeatInterval).subscribe(
            (iteration) => {
              const view = new ThasViewEvent(
                this.router.url,
                iteration,
                'viewed',
                document.hasFocus() ? 'active' : 'inactive'
              );

              this.#client.send(view);
            }
          );
        }
      });
  }

  /**
   * Create instance of a `ThasClient` with correct configuration
   */
  #getThasClient(config: AnalyticsServiceConfig): ThasClient {
    if (window['ThasClient']) {
      return window['ThasClient'];
    }

    return new ThasClient({
      ...config,
      locationEventsEnabled: false,
      windowAccessEnabled: false,
    });
  }
}
